import React, { useEffect, useRef, useState } from 'react';
import { Award, BookOpen, Globe, Users } from 'lucide-react';
import { Card } from './ui/card';

const Projects = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const currentRef = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const projects = [
    {
      icon: BookOpen,
      title: 'Corpus-Based Research on Pakistani English',
      description: 'Led postdoctoral and funded research projects focusing on learner corpora and linguistic variation in Pakistani English, contributing to the understanding of World Englishes.',
      impact: 'Advanced understanding of linguistic variation in South Asian English'
    },
    {
      icon: Globe,
      title: 'International Corpus Linguistics Workshops',
      description: 'Secured British Council and HEC research grants to organize international workshops on corpus-based research, bringing global scholars to South Asia and building regional research capacity.',
      impact: 'Enhanced regional research capacity and international collaboration'
    },
    {
      icon: Award,
      title: 'Books on Narrative Discourse & Pedagogy',
      description: 'Authored and published scholarly books on Qurānic and Biblical narrative discourse and culturally appropriate language teaching materials, widely used by researchers and educators.',
      impact: 'Influential contributions to narrative studies and pedagogy'
    },
    {
      icon: Users,
      title: 'Academic Leadership & Institutional Development',
      description: 'Played a central role in academic governance, policy development, and quality assurance at IIUI, influencing curriculum reform and research standards across faculties.',
      impact: 'Transformed institutional academic standards and research culture'
    }
  ];

  return (
    <section id="projects" ref={sectionRef} className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-3">
            Selected Projects & Achievements
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto mb-4"></div>
          <p className="text-gray-600 max-w-2xl mx-auto text-sm">
            Highlighting key contributions to linguistics research, academic development, and international collaboration
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {projects.map((project) => {
            const Icon = project.icon;
            return (
              <Card
                key={project.title}
                className={`p-6 bg-white border-gray-200 hover:border-blue-600 hover:shadow-xl transition-all duration-500 group ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
              >
                <div className="flex flex-col h-full">
                  <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-600 transition-colors duration-300">
                    <Icon className="text-blue-600 group-hover:text-white transition-colors duration-300" size={28} />
                  </div>
                  <h3 className="font-bold text-lg text-gray-900 mb-3">
                    {project.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-3 flex-grow text-sm">
                    {project.description}
                  </p>
                  <div className="pt-3 border-t border-gray-200">
                    <p className="text-xs text-blue-700 font-medium">
                      <span className="text-gray-500">Impact:</span> {project.impact}
                    </p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Projects;